/**
 * «Прошлый период» в «Обзоре»: итог и разбор (docs/ANALYTICS_PERIOD.md §2.5).
 *
 * Итог — у всех. Разбор — Pro и пробный период: одно мягкое наблюдение по
 * свободным тратам, «Попробую» / «Не про меня» / «Не сейчас», итоги прошлых
 * договорённостей. Числа считает ядро (`periodReview`), слова — шаблон или
 * модель с проверкой чисел (`useReviewWords`).
 */
import { useEffect, useMemo, useState } from 'react';
import {
  diffDays, formatMoney, periodReview, type DealResult, type Observation, type PeriodReview, type ReportPeriod,
} from '@skoka/core';
import { useAccess } from './access.js';
import { useOpenPro } from './pro-context.js';
import { Icon } from './icon.js';
import {
  addPeriodDeal, dropPeriodDeal, hidePeriodTopic, laterPeriodReview, showPeriodTopic, type AppState,
} from './store.js';
import { SheetWrap } from './sheet.js';
import { dayMonth, plural } from './text.js';
import { useReviewWords } from './period-review-words.js';

const short = (p: ReportPeriod, today: string) => `${dayMonth(p.from, today as never)} – ${dayMonth(p.to ?? p.from, today as never)}`;

/** «23 покупки, почти через день» — частота словами, если она заметна. */
const often = (o: Observation, p: ReportPeriod): string => {
  const days = diffDays(p.from, p.to ?? p.from) + 1;
  const per = days / Math.max(1, o.count);
  return per <= 1.3 ? ', почти каждый день' : per <= 2.5 ? ', почти через день' : '';
};

/** Текст наблюдения по шаблону — им же проверяются слова модели. */
export function observationText(o: Observation, p: ReportPeriod): string {
  const goal = o.goal
    ? ` Отложенные в «${o.goal.name}», они приблизят её примерно на ${plural(o.goal.months, 'месяц', 'месяца', 'месяцев').replace(/^1 месяц$/, 'месяц')}.`
    : '';
  return `На «${o.name}» ушло ${formatMoney(o.amount)} — ${plural(o.count, 'покупка', 'покупки', 'покупок')}${often(o, p)}. `
    + `Обычно выходит около ${formatMoney(o.usual)}. Если в этом периоде оставить на это около ${formatMoney(o.target)}, `
    + `потратишь на ${formatMoney(o.less)} меньше, чем в прошлом.${goal}`;
}

/** Итог договорённости словами. */
function dealText(d: DealResult): string {
  if (d.kept) {
    const less = d.less > 0 ? ` — на ${formatMoney(d.less)} меньше прошлого периода` : '';
    return `«${d.name}»: ${formatMoney(d.spent)}${less}. Договаривались примерно на ${formatMoney(d.deal.target)} — уложились.`;
  }
  return `В прошлый раз решили попробовать оставить на «${d.name}» около ${formatMoney(d.deal.target)}. `
    + `Вышло ${formatMoney(d.spent)}. Так бывает: привычки не меняются за один период.`;
}

export function PeriodReviewCard({ state, periods, onChanged, onOperations }: {
  state: AppState;
  /** Периоды, по которым разбирать: от зарплаты, а без неё — месяцы. */
  periods: readonly ReportPeriod[];
  onChanged: () => void;
  /** «Посмотреть операции» категории за разобранный период. */
  onOperations: (periodKey: string, categoryName: string) => void;
}) {
  // Пока доступ не узнан (или нет сети) — только итог, без «в Pro»: Pro-пользователю его не показываем.
  const access = useAccess();
  const openPro = useOpenPro();
  const [more, setMore] = useState(false);
  const [wrong, setWrong] = useState<Observation | DealResult | null>(null);
  const [hidden, setHidden] = useState(false);
  const review: PeriodReview | null = useMemo(() => periodReview({
    periods, txs: state.transactions, categories: state.categories, goals: state.goals,
    goalEvents: state.goalEvents, creditCardIds: state.accounts.filter((a) => a.kind === 'credit_card').map((a) => a.id),
    deals: state.periodDeals, topicsOff: state.periodTopicsOff,
  }), [state, periods]);
  const main = review?.observations[0];
  const pro = access?.tier === 'pro' || access?.tier === 'trial';
  const words = useReviewWords(review, main, pro);

  // Три периода подряд уложились — договорённость больше не держим.
  useEffect(() => {
    const done = review?.deals.filter((d) => d.streak >= 3) ?? [];
    if (done.length === 0) return;
    void Promise.all(done.map((d) => dropPeriodDeal(d.deal.categoryId))).then(onChanged);
  }, [review, onChanged]);

  if (!review) return null;
  const p = review.period;
  const later = state.periodReviewLater.includes(p.key);
  const trio = (
    <div className="an-trio">
      <div><span>Пришло</span><b className="num">{formatMoney(review.income)}</b></div>
      <div><span>Потрачено</span><b className="num">{formatMoney(review.spent)}</b></div>
      <div><span>Отложено</span><b className="num">{formatMoney(review.saved)}</b></div>
    </div>
  );
  const head = (
    <div className="an-top"><b>Прошлый период{pro ? <span className="tag pro">Pro</span> : null}</b>
      <span className="an-dates">{short(p, state.today)}</span></div>
  );

  const agree = async (o: Observation) => {
    await addPeriodDeal({ categoryId: o.categoryId, periodKey: p.key, usual: o.usual, target: o.target, at: new Date().toISOString() });
    onChanged();
  };
  const actions = (o: Observation) => (
    <div className="an-btnrow">
      <button className="btn pri" disabled={!review.canPropose} onClick={() => void agree(o)}>Попробую</button>
      <button className="btn" onClick={() => setWrong(o)}>Не про меня</button>
    </div>
  );

  if (!pro) {
    return (
      <section className="blk an-review">
        {head}
        {trio}
        {/*
          Наблюдение считает само приложение, модель только пишет его словами —
          поэтому показать, что оно есть, ничего не стоит (21.10.2026). Текст
          размыт: что именно нашлось, откроется в Pro.
        */}
        {access && !access.offline && (main ? (
          <div className="tease-card">
            <div className="tease-h"><Icon name="bullseye-arrow" size={15} /><b>Нашлось наблюдение про свободные траты</b></div>
            <p className="tease-blur" aria-hidden="true">{observationText(main, review.period)}</p>
            <button className="btn pri" onClick={() => openPro('review')}>Открыть разбор — Pro</button>
          </div>
        ) : (
          <>
            <div className="note">Разбор периода — в Pro: одно наблюдение по свободным тратам и что оно даст целям.</div>
            <button className="btn" onClick={() => openPro('review')}>Что даёт Pro</button>
          </>
        ))}
      </section>
    );
  }

  if (later) {
    return (
      <button className="li w an-review-line" onClick={() => { void laterPeriodReview(p.key, false).then(onChanged); }}>
        <div className="nm"><b>Разбор прошлого периода</b><span>{short(p, state.today)} · показать</span></div>
      </button>
    );
  }

  // Договорились по итогам этого периода: наблюдение ядро уже не выдаёт — показываем, о чём договорились.
  const agreed = state.periodDeals.filter((d) => !d.droppedAt && d.periodKey === p.key);
  const nameOf = (id: string) => state.categories.find((c) => c.id === id)?.name ?? 'Категория';
  return (
    <section className="blk an-review">
      {head}
      {trio}
      {review.deals.map((d) => (
        <div className="an-obs" key={`deal:${d.deal.categoryId}`}>
          <p>{dealText(d)}{d.streak >= 3 ? ' Три периода подряд — больше не напоминаю.' : ''}</p>
          {!d.kept && (
            <div className="an-btnrow">
              <button className="btn pri" onClick={async () => {
                await dropPeriodDeal(d.deal.categoryId);
                await addPeriodDeal({ ...d.deal, periodKey: p.key, at: new Date().toISOString() });
                onChanged();
              }}>Попробую ещё</button>
              <button className="btn" onClick={() => setWrong(d)}>Не про меня</button>
            </div>
          )}
        </div>
      ))}
      {agreed.map((d) => (
        <div className="okline" key={`agreed:${d.categoryId}`}>
          Договорились: оставить на «{nameOf(d.categoryId)}» около {formatMoney(d.target)} в этом периоде.
        </div>
      ))}
      {main && (
        <div className="an-obs">
          <p>{words ?? observationText(main, p)}</p>
          {review.canPropose
            ? actions(main)
            : <div className="note">Уже две договорённости — новых не предлагаю, пока не подведём итоги.</div>}
        </div>
      )}
      {!main && review.deals.length === 0 && agreed.length === 0 && (
        <div className="note">Заметных перемен в свободных тратах нет — разбирать нечего.</div>
      )}
      {main && <button className="lnk" onClick={() => { void laterPeriodReview(p.key, true).then(onChanged); }}>Не сейчас</button>}
      {review.observations.length > 1 && (
        <>
          <button className="lnk more" onClick={() => setMore(!more)}>
            {more ? 'Свернуть' : `Ещё ${plural(review.observations.length - 1, 'наблюдение', 'наблюдения', 'наблюдений')}`}
          </button>
          {more && review.observations.slice(1).map((o) => (
            <div className="an-obs" key={o.categoryId}>
              <p>{observationText(o, p)}</p>
              {review.canPropose ? actions(o) : null}
            </div>
          ))}
        </>
      )}
      {state.periodTopicsOff.length > 0 && (
        <button className="lnk" onClick={() => setHidden(true)}>
          Не поднимаю: {state.periodTopicsOff.map((t) => nameOf(t.categoryId)).join(', ')}
        </button>
      )}
      {hidden && (
        <SheetWrap title="Не поднимаю в разборе" onClose={() => setHidden(false)}>
          <div className="form">
            <div className="note" style={{ marginTop: 0 }}>Эти траты отмечены как твои решения. Вернёшь — снова буду сравнивать с обычным.</div>
            {state.periodTopicsOff.map((t) => (
              <div className="li w" key={t.categoryId}>
                <div className="nm"><b>{nameOf(t.categoryId)}</b><span>{t.reason === 'work' ? 'Рабочие расходы' : 'Мне это важно'}</span></div>
                <button className="btn" onClick={async () => {
                  await showPeriodTopic(t.categoryId);
                  if (state.periodTopicsOff.length <= 1) setHidden(false);
                  onChanged();
                }}>Вернуть</button>
              </div>
            ))}
            <button className="btn" onClick={() => setHidden(false)}>Готово</button>
          </div>
        </SheetWrap>
      )}
      {wrong && (
        <NotForMe item={wrong} onClose={() => setWrong(null)}
          onMine={async (reason) => {
            const id = 'deal' in wrong ? wrong.deal.categoryId : wrong.categoryId;
            if ('deal' in wrong) await dropPeriodDeal(id);
            await hidePeriodTopic(id, reason);
            setWrong(null);
            onChanged();
          }}
          onData={async () => {
            const name = wrong.name;
            if ('deal' in wrong) await dropPeriodDeal(wrong.deal.categoryId);
            setWrong(null);
            onChanged();
            onOperations(p.key, name);
          }} />
      )}
    </section>
  );
}

/** «Что здесь не так?» — макет 4. Свободный текст модели не отправляется. */
function NotForMe({ item, onClose, onMine, onData }: {
  item: Observation | DealResult;
  onClose: () => void;
  onMine: (reason: 'important' | 'work') => void;
  onData: () => void;
}) {
  const [pick, setPick] = useState<'important' | 'work' | 'sum' | 'mixed' | null>(null);
  const [own, setOwn] = useState('');
  const mine = pick === 'important' || pick === 'work';
  return (
    <SheetWrap title="Что здесь не так?" onClose={onClose}>
      <div className="form">
        <div className="note" style={{ marginTop: 0 }}>Свою жизнь ты знаешь лучше. Отметь, что не так, — объяснять необязательно.</div>
        <div className="pick"><div className="k">Это моё решение</div></div>
        <div className="chips">
          <button className={`chip${pick === 'important' ? ' on' : ''}`} onClick={() => setPick('important')}>Мне это важно</button>
          <button className={`chip${pick === 'work' ? ' on' : ''}`} onClick={() => setPick('work')}>Это рабочие расходы</button>
        </div>
        <div className="note">Больше не буду поднимать «{item.name}».</div>
        <div className="pick"><div className="k">В данных ошибка</div></div>
        <div className="chips">
          <button className={`chip${pick === 'sum' ? ' on' : ''}`} onClick={() => setPick('sum')}>Сумма неверная</button>
          <button className={`chip${pick === 'mixed' ? ' on' : ''}`} onClick={() => setPick('mixed')}>Тут не только {item.name.toLowerCase()}</button>
        </div>
        <div className="note">Покажу операции «{item.name}» за период — поправишь, и я пересчитаю. Тему при этом не прячу.</div>
        <textarea value={own} onChange={(e) => setOwn(e.target.value)} placeholder="Или своими словами — необязательно" rows={2} />
        <button className="btn pri" disabled={!pick}
          onClick={() => (mine ? onMine(pick as 'important' | 'work') : onData())}>
          {mine ? 'Больше не поднимать' : 'Посмотреть операции'}
        </button>
        <button className="btn" onClick={onClose}>Отмена</button>
      </div>
    </SheetWrap>
  );
}
